import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaAnalyticsGateway1Service } from '../service';
import { GetAppointmentsNsrDataIO } from '../types';

export const getGetAppointmentsNsrDataQueryKey = (req: GetAppointmentsNsrDataIO['input']) =>
  ['AnalyticsGatewayV1', 'GetAppointmentsNsrData', req] as const;
type QueryKey = ReturnType<typeof getGetAppointmentsNsrDataQueryKey>;

/**
 * A hook that returns a query for the `GetAppointmentsNsrData` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetAppointmentsNsrDataQuery = <E = unknown, D = GetAppointmentsNsrDataIO['output']>(
  req: GetAppointmentsNsrDataIO['input'],
  options?: LimitedSchemaQueryOptions<GetAppointmentsNsrDataIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetAppointmentsNsrDataIO['output'], E, D, QueryKey>({
    queryKey: getGetAppointmentsNsrDataQueryKey(req),
    queryFn: () => SchemaAnalyticsGateway1Service.GetAppointmentsNsrData(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetAppointmentsNsrData` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetAppointmentsNsrDataQueries = <E = unknown, D = GetAppointmentsNsrDataIO['output']>(
  argsArr: {
    req: GetAppointmentsNsrDataIO['input'];
    options?: LimitedSchemaQueryOptions<GetAppointmentsNsrDataIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetAppointmentsNsrDataQueryKey(req),
      queryFn: () => SchemaAnalyticsGateway1Service.GetAppointmentsNsrData(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetAppointmentsNsrDataQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `GetAppointmentsNsrData` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetAppointmentsNsrData = () => {
  const queryClient = useQueryClient();

  const fetchGetAppointmentsNsrData = async <E = unknown, D = GetAppointmentsNsrDataIO['output']>(
    req: GetAppointmentsNsrDataIO['input'],
    options?: LimitedSchemaFetchQueryOptions<GetAppointmentsNsrDataIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<GetAppointmentsNsrDataIO['output'], E, D, QueryKey>(
      getGetAppointmentsNsrDataQueryKey(req),
      () => SchemaAnalyticsGateway1Service.GetAppointmentsNsrData(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetAppointmentsNsrData;
};
